import { useEffect, useMemo, useCallback } from 'react'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { useAccountContext } from '../contexts/AccountContext'

// Validate and parse a URL timestamp (in seconds), returning milliseconds
export function parseTimestamp(value: string | null): number | undefined {
  if (!value) return undefined
  const num = parseInt(value, 10)
  if (isNaN(num) || num <= 0) return undefined
  // Sanity check: timestamp should be between year 2000 and 2100
  const minTimestamp = 946684800 // 2000-01-01
  const maxTimestamp = 4102444800 // 2100-01-01
  if (num < minTimestamp || num > maxTimestamp) return undefined
  return num * 1000 // Convert to milliseconds
}

/**
 * Hook to synchronize device selection and time range between URL query parameters and global state.
 *
 * URL parameters:
 * - sigfoxId: Device identifier (6 character hex string)
 * - from: Start timestamp in seconds (Unix epoch)
 * - to: End timestamp in seconds (Unix epoch)
 *
 * When the page loads or the URL changes (e.g., browser back/forward), the state is updated.
 * This ensures device selection and time range persist across page refreshes and navigation.
 */
export function useDeviceUrlSync() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedDevice: selectedDeviceId, selectDeviceLocally, devices } = useAccountContext()

  const sigfoxIdFromUrl = searchParams.get('sigfoxId') || ''
  const fromParam = searchParams.get('from')
  const toParam = searchParams.get('to')

  // Validate sigfoxId format (6 character hex string)
  const isValidSigfoxId = (id: string): boolean => {
    return /^[0-9A-Fa-f]{6}$/.test(id)
  }

  // Parse time range from URL (converted to milliseconds)
  const urlTimeRange = useMemo(() => {
    const fromMs = parseTimestamp(fromParam)
    const toMs = parseTimestamp(toParam)

    // Both must be valid and from < to
    if (fromMs && toMs && fromMs < toMs) {
      // Cap 'to' at current time
      const cappedTo = Math.min(toMs, Date.now())
      return { from: fromMs, to: cappedTo }
    }
    return undefined
  }, [fromParam, toParam])

  // Sync the URL's sigfoxId parameter INTO the global selected device state.
  //
  // We read the LIVE url (window.location.search) here rather than the router's
  // cached `sigfoxIdFromUrl`. Map markers update the address bar with
  // history.replaceState (see MapPage) to keep the pan smooth, which the router
  // doesn't observe, so its cached value goes stale. Comparing against the live
  // URL means that when a marker click changes selectedDeviceId this effect sees
  // the URL already matches the new selection and does nothing, instead of snapping
  // the selection back to the previous device (which used to break marker switching).
  //
  // Keeping selectedDeviceId in the deps is load-bearing: MapPage clears the
  // selection on mount, and this effect re-firing on that change is what restores
  // the device from the URL on refresh / direct link.
  useEffect(() => {
    const liveSigfoxId = new URLSearchParams(window.location.search).get('sigfoxId') || ''
    if (liveSigfoxId && isValidSigfoxId(liveSigfoxId) && liveSigfoxId !== selectedDeviceId) {
      selectDeviceLocally(liveSigfoxId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sigfoxIdFromUrl, selectedDeviceId])

  // Helper function to update URL when device is selected programmatically
  const updateDeviceInUrl = (sigfoxId: string) => {
    if (isValidSigfoxId(sigfoxId)) {
      navigate(`${location.pathname}?sigfoxId=${sigfoxId}`, { replace: true })
    }
  }

  // Helper function to update time range in URL (timestamps in milliseconds, converted to seconds for URL)
  const updateTimeRangeInUrl = useCallback(
    (fromMs: number, toMs: number) => {
      const fromSeconds = Math.round(fromMs / 1000)
      const toSeconds = Math.round(toMs / 1000)

      const newParams = new URLSearchParams(searchParams)
      newParams.set('from', fromSeconds.toString())
      newParams.set('to', toSeconds.toString())
      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  // Get the current selected device from context or URL
  // Memoize to prevent creating new objects on every render
  const selectedDevice = useMemo(() => {
    if (sigfoxIdFromUrl && isValidSigfoxId(sigfoxIdFromUrl)) {
      // Try to find device in devices list
      const devicesArray = Object.values(devices)
      const device = devicesArray.find((d: any) => d.sigfoxId === sigfoxIdFromUrl)
      if (device) {
        return device
      }
      // Create a minimal device object with just the sigfoxId if not found
      // Using 'any' type here since this is a fallback for unknown devices
      return { sigfoxId: sigfoxIdFromUrl, deviceName: 'Unknown Device' } as any
    } else if (selectedDeviceId) {
      return devices[selectedDeviceId]
    }
    return null
  }, [sigfoxIdFromUrl, selectedDeviceId, devices])

  const sigfoxId = selectedDevice?.sigfoxId || null

  return {
    selectedDevice,
    sigfoxId,
    sigfoxIdFromUrl,
    isValidSigfoxId,
    updateDeviceInUrl,
    urlTimeRange,
    updateTimeRangeInUrl,
  }
}
